import classnames from 'classnames';
import * as _ from 'lodash';
import { Component } from 'react';
import { Modal } from 'react-bootstrap';
import type { FunctionComponent, ReactElement } from 'react';

import { renderPackage, renderRevision } from '../../../shared/helpers/renderLocator';
import Locator from '../../../shared/Locator';
import { getLicense } from '../../api/licenses';
import { getRevisionLicenses } from '../../api/revisions';
import { errorMessageFromResponse } from '../../helpers/errors';
import { get, put } from '../../redux/api';
import { getMultiLicenseTitle, hasError, isConcludedLicense, isDeclaredLicense, nuc } from '../../utils';
import { ErrorAlert } from '../generic';
import Icon, { icons } from '../generic/Icon';
import Loader from '../generic/Loader';
import Select from '../generic/Select/Select';
import { groupMatches, groupRevisionLicense } from '../licenses/groups';
import LicenseDetail from '../licenses/LicenseDetail';
import LicenseMatchList from '../projects/issues/LicenseMatchList';
import type { Match, RevisionLicense } from '../../../shared/licenses/common';
import type { AsyncStatus, License, ListMatches, SelectOption } from '../../types';
import type { NormalizedLicenseGroup, NormalizedMatch } from '../projects/issues/LicenseMatchList';

import ModalBrowseMatch from './ModalBrowseMatch';
import ModalSlidePane from './ModalSlidePane';

const NoSummaryFound = () => (
  <p>
    <i>No Summary Found</i>
  </p>
);

const Tab: FunctionComponent<{
  isActive: boolean;
  text: string;
  onClick: () => void;
}> = ({ isActive, text, onClick }) => (
  <li className={classnames({
    active: isActive,
  })}>
    <a onClick={onClick} role="button">
      {text}
    </a>
  </li>
);

const sortLicenseGroupsByMostMatches = (groupA: NormalizedLicenseGroup, groupB: NormalizedLicenseGroup): number => {
  const matchesA = groupA.matches.length + (groupA.isDeclared ? 1 : 0);
  const matchesB = groupB.matches.length + (groupB.isDeclared ? 1 : 0);
  if (matchesA > matchesB) {
    return -1;
  }
  if (matchesB > matchesA) {
    return 1;
  }
  return 0;
};

const sortMatchesByDeclaredLicenseFirst = (a: NormalizedMatch, b: NormalizedMatch): number => {
  if (isDeclaredLicense(a)) {
    return -1;
  }
  if (isDeclaredLicense(b)) {
    return 1;
  }
  return 0;
};

const reduceMatchesByPath = (acc: NormalizedMatch[], match: Match): NormalizedMatch[] => {
  if (!match.path || isDeclaredLicense(match) || isConcludedLicense(match)) {
    return acc;
  }

  const existingMatchIndex = acc.findIndex(({ path }) => path === match.path);
  if (existingMatchIndex !== -1) {
    // Update the existing match with additional match data.
    return acc.map((normMatch, i) => {
      if (i === existingMatchIndex) {
        return {
          path: normMatch.path,
          originalMatches: [...normMatch.originalMatches, match],
          sources: _.union(normMatch.sources, match.sources || []),
        };
      }

      return normMatch;
    });
  }

  // Add a new match's data.
  return [...acc, {
    path: match.path,
    originalMatches: [match],
    sources: match.sources || [],
  }];
};

const matchGroupLicenseIds = (matchGroup: Match[]): string[] =>
  matchGroup.map(match => match.RevisionLicense.licenseId as string);

const matchGroupRevisionLicenseIds = (matchGroup: Match[]): number[] =>
  matchGroup.map(match => match.RevisionLicense.id);

/**
 * Finds match groups for a given RevisionLicense group, where the match groups' license IDs are a superset
 * of the RevisionLicense group's license IDs. This function is specifically for non-custom licenses.
 */
const findMatchGroupsForNonCustomLicenses = (group:RevisionLicense[], matchGroups: Match[][]): Match[] => {
  // Because of ProjectCorrections, we can't lay our matches over a RevisionLicense group simply by considering each
  // match's revisionLicenseId property, so we rely on the licenseId instead.
  const groupLicenseIds = group.map(({ licenseId }) => licenseId as string);

  // Keep the match groups whose license IDs are a superset of this RevisionLicense group's license IDs. This is
  // looser than keeping only the match groups whose license IDs exactly match this RevisionLicense group's license
  // IDs, and this helps with viewing matches in the presence of a ProjectCorrection.
  return matchGroups
    .filter((matchGroup) =>
      groupLicenseIds.every((licenseId) => matchGroupLicenseIds(matchGroup).includes(licenseId)),
    )
    .flat();
};

/**
 * Finds match groups for a given RevisionLicense group, where the match groups' revisionLicenseIds are a superset
 * of the RevisionLicense group's revisionLicenseIds. This function is specifically for custom licenses.
 *
 * Context: Custom licenses share the same "custom-license" license id, despite being different licenses. For this reason, we can't
 * rely on the licenseId property to match a RevisionLicense group with its match groups. If we insisted on using the
 * licenseId field, we would end up with the same set of matches for all custom licenses.
 */
const findMatchGroupsForCustomLicenses = (group:RevisionLicense[], matchGroups: Match[][]): Match[] => {
  // Unlike predefined licenses in our system, custom licenses share the same "custom-license" license id, despite being
  // different licenses. For this reason, we can't rely on the licenseId property to match a RevisionLicense group
  // with its match groups. Instead, we rely on the revisionLicenseId property.
  const revisionLicenseIds = group.map(({ id }) => id);

  return matchGroups
    .filter((matchGroup) =>
      revisionLicenseIds.every(
        (revisionLicenseId) => matchGroupRevisionLicenseIds(matchGroup).includes(revisionLicenseId),
      ),
    )
    .flat();
};

// revisionLicenses is all the RevisionLicenses that we could render for a revision.
const normalizeLicensesIntoGroups = (
  revisionLicenses: RevisionLicense[],
  matches: Match[],
): NormalizedLicenseGroup[] => {
  // matchGroups is all the matches grouped by the LicenseGroup IDs.
  const matchGroups = groupMatches(matches);

  return groupRevisionLicense(revisionLicenses)
    .map((group: RevisionLicense[]): NormalizedLicenseGroup => {
      const firstLicense = _.head(group) as RevisionLicense;

      // Find the match groups base on the current RevisionLicense group.
      // We can't rely on the matches field in the firstLicense because it is missing the sources field, which is
      // crucial for rendering the ModalBrowseMatch component.
      const revisionLicenseMatches =
        firstLicense.licenseId === 'custom-license'
          ? findMatchGroupsForCustomLicenses(group, matchGroups)
          : findMatchGroupsForNonCustomLicenses(group, matchGroups);

      return {
        groupId: firstLicense.licenseGroupId || { revisionLicenseId: firstLicense.id },
        revisionLicenses: group,
        licenseName: getMultiLicenseTitle(group),
        isDeclared: revisionLicenseMatches.some(isDeclaredLicense),
        isConcluded: revisionLicenseMatches.some(isConcludedLicense),
        // Take the match groups whose RevisionLicenses overlap exactly with the current RevisionLicense group.
        matches: revisionLicenseMatches
          .reduce(reduceMatchesByPath, [] as NormalizedMatch[])
          .sort(sortMatchesByDeclaredLicenseFirst),
      };
    })
    .sort(sortLicenseGroupsByMostMatches);
};

enum TabState {
  Matches,
  FullText,
}

// In single revision mode, we show only licenses passed in as the revisionLicenses array, which should include licenses
// only from a single license group.
export type SingleRevisionDisplayMode = {
  mode: 'single';
  revisionId: string;
  revisionLicenses: RevisionLicense[];
};

// In multiple revisions mode, we show all the licenses found in each revision specified in the matchRevisionIds array.
// The licenseId property is the license in focus, whose metadata we fetch and display.
export type MultipleRevisionsDisplayMode = {
  mode: 'multiple';
  matchRevisionIds: string[];
  licenseId: string;
};

type IModalDisplayLicenseProps = {
  // The modal is shown only when displayMode is not null.
  displayMode: null | SingleRevisionDisplayMode | MultipleRevisionsDisplayMode;
  revisionPackageCacheError?: string;
  onHide(): void;
};

type IModalDisplayLicenseState = {
  tabState: TabState;
  selectedRevisionId: string | null;
  // showingLicenseGroup indicates what's being shown in the ModalBrowseMatch component that we render here.
  showingLicenseGroup: NormalizedLicenseGroup | null;
  currentPath: string | null;
  licenses: License[];
  licensesStatus: AsyncStatus;
  revisionLicensesStatus: AsyncStatus;
  matchesStatus: AsyncStatus;
  ignoreMatchesStatus: AsyncStatus;
  normalizedLicenseGroups: NormalizedLicenseGroup[];
  cliLicenseScanWithMatchData: boolean;
};

class ModalDisplayLicense extends Component<IModalDisplayLicenseProps, IModalDisplayLicenseState> {
  readonly state: IModalDisplayLicenseState = {
    tabState: TabState.Matches,
    selectedRevisionId: null,
    showingLicenseGroup: null,
    currentPath: null,
    licenses: [],
    licensesStatus: { loading: false },
    revisionLicensesStatus: { loading: false },
    matchesStatus: { loading: false },
    ignoreMatchesStatus: { loading: false },
    normalizedLicenseGroups: [],
    cliLicenseScanWithMatchData: false,
  };

  componentDidUpdate(prevProps: Readonly<IModalDisplayLicenseProps>, prevState: Readonly<IModalDisplayLicenseState>) {
    const { props, state } = this;
    const { displayMode } = props;

    if (displayMode && !prevProps.displayMode) {
      if (displayMode.mode === 'single') {
        void this.onLoadLicenses(displayMode.revisionLicenses.map(({ licenseId }) => licenseId as string));
      } else {
        void this.onLoadLicenses([displayMode.licenseId]);
      }
    }

    // Load RevisionLicenses and Matches when the selectedRevisionId is changed and the modal is open in multiple
    // revisions mode.
    const { selectedRevisionId } = state;
    if (displayMode?.mode === 'multiple' && selectedRevisionId && selectedRevisionId !== prevState.selectedRevisionId) {
      this.setState({
        tabState: TabState.Matches,
        currentPath: null,
        normalizedLicenseGroups: [],
      });
      void (async () => {
        const [revisionLicenses, matches] = await Promise.all([
          this.onLoadRevisionLicenses(selectedRevisionId),
          this.onLoadMatches(selectedRevisionId),
        ]);
        this.setState({
          normalizedLicenseGroups: normalizeLicensesIntoGroups(revisionLicenses, matches),
        });
      })();
    }

    // Load Matches when the modal is opened in single revision mode and take the RevisionLicenses passed in to
    // construct normalized license groups.
    if (displayMode?.mode === 'single' && !prevProps.displayMode) {
      this.setState({
        tabState: TabState.Matches,
        selectedRevisionId: displayMode.revisionId,
        normalizedLicenseGroups: [],
      });
      void (async () => {
        const matches = await this.onLoadMatches(displayMode.revisionId);
        this.setState({
          normalizedLicenseGroups: normalizeLicensesIntoGroups(displayMode.revisionLicenses, matches),
        });
      })();
    }

    if (displayMode?.mode === 'multiple' && !prevProps.displayMode) {
      // The modal was just opened. Select a revision ID from the matchRevisionIds array.

      this.setState({
        tabState: TabState.Matches,
        selectedRevisionId: _.head(displayMode.matchRevisionIds) as string,
      });
    }
  }

  onLoadRevisionLicenses = async (revisionId: string): Promise<RevisionLicense[]> => {
    this.setState({
      revisionLicensesStatus: { loading: true },
    });
    try {
      const revisionLicenses: RevisionLicense[] = await getRevisionLicenses(revisionId);
      this.setState({
        revisionLicensesStatus: { loading: false },
      });
      return revisionLicenses;
    } catch (err) {
      this.setState({
        revisionLicensesStatus: {
          error: `Could not load license data for the selected revision: ${errorMessageFromResponse(err)}`,
          loading: false,
        },
      });
      return [];
    }
  };

  onLoadLicenses = async (licenseIds: string[]) => {
    this.setState({
      licenses: [],
      licensesStatus: { loading: true },
    });
    try {
      const licenses: License[] = await Promise.all(licenseIds.map(getLicense));
      this.setState({
        licenses,
        licensesStatus: { loading: false },
      });
    } catch (err) {
      this.setState({
        licensesStatus: {
          error: `Could not load license data: ${errorMessageFromResponse(err)}`,
          loading: false,
        },
      });
    }
  };

  onLoadMatches = async (revisionId: string): Promise<Match[]> => {
    this.setState({
      matchesStatus: { loading: true },
    });
    try {
      const resp = await get<ListMatches>(
        '/api/matches/',
        false,
        {
          // Fetch all matches for the selected revision.
          revisionId: nuc(revisionId),
        },
      );

      if (!resp.data) {
        throw new Error('No match data found.');
      }

      const matchData: ListMatches = resp.data;

      this.setState({
        matchesStatus: { loading: false },
        cliLicenseScanWithMatchData: matchData.cliLicenseScanWithMatchData,
      });
      return matchData.matches;
    } catch (err) {
      this.setState({
        matchesStatus: {
          loading: false,
          error: `Could not load license match data: ${errorMessageFromResponse(err)}`,
        },
      });
      return [];
    }
  };

  onBulkIgnoreMatches = async (matches: NormalizedMatch[]) => {
    this.setState({
      ignoreMatchesStatus: { loading: true },
    });

    const ignoredMatchIds = matches.flatMap(({ originalMatches }) => originalMatches.map(({ id }) => id));

    try {
      await put('/api/matches/bulk_ignore', {
        all_ids: ignoredMatchIds,
      });
      this.setState((prevState: IModalDisplayLicenseState) => {
        const normalizedLicenseGroups = prevState.normalizedLicenseGroups.map((group): NormalizedLicenseGroup => ({
          ...group,
          // Clear out ignored matches.
          matches: _.compact<NormalizedMatch>(
            group.matches.map((match): NormalizedMatch | null => {
              const remainingMatches = match.originalMatches.filter(({ id }) => !ignoredMatchIds.includes(id));
              if (!remainingMatches.length) {
                // Discard this NormalizedMatch because all of its Matches were ignored.
                return null;
              }

              return {
                ...match,
                originalMatches: remainingMatches,
                sources: _.uniq(remainingMatches.flatMap(({ sources }) => sources || [])),
              };
            }),
          ),
        }));

        let { showingLicenseGroup, currentPath } = prevState;
        if (showingLicenseGroup) {
          showingLicenseGroup = normalizedLicenseGroups.find(
            group => _.isEqual(group.groupId, showingLicenseGroup.groupId),
          ) || null;
          // If all matches were ignored in the license group currently showing, close the ModalBrowseMatch modal.
          if (!showingLicenseGroup?.matches.length) {
            showingLicenseGroup = null;
            currentPath = null;
          } else if (!showingLicenseGroup.matches.some(({ path }) => path === currentPath)) {
            currentPath = (_.head(showingLicenseGroup.matches) as NormalizedMatch).path;
          }
        }

        return {
          showingLicenseGroup,
          currentPath,
          ignoreMatchesStatus: { loading: false },
          normalizedLicenseGroups,
        };
      });
    } catch (err) {
      this.setState({
        ignoreMatchesStatus: {
          loading: false,
          error: `Could not ignore matches: ${errorMessageFromResponse(err)}`,
        },
      });
    }
  };

  onSelectRevision = (selectedRevisionId: string) => {
    this.setState({ selectedRevisionId });
  };

  onTabClick = (tabState: TabState) => () => this.setState({ tabState });

  onHideModalBrowseMatch = () => this.setState({
    showingLicenseGroup: null,
    currentPath: null,
  });

  get modalTitle(): string {
    const { displayMode } = this.props;
    if (!displayMode) {
      return '';
    }

    // In single revision mode, we display the custom title or licenseId of the currently active license group.
    if (displayMode.mode === 'single') {
      return getMultiLicenseTitle(displayMode.revisionLicenses);
    }

    // In multiple revisions mode, we display only the active license's actual title because there isn't one static set
    // of RevisionLicenses for all the revisions that have this license.
    return displayMode.licenseId;
  }

  isTabActive = (tab) => this.state.tabState === tab;

  // In multiple revisions mode, filteredLicenseGroups returns only the license groups that have at least one licenseId
  // that matches the licenseId specified in the displayMode prop. Otherwise this function returns all groups.
  get filteredLicenseGroups(): NormalizedLicenseGroup[] {
    const { displayMode } = this.props;
    const { normalizedLicenseGroups } = this.state;

    if (displayMode?.mode === 'multiple') {
      return normalizedLicenseGroups.filter(
        ({ revisionLicenses }) => revisionLicenses.some(({ licenseId }) => licenseId === displayMode.licenseId),
      );
    }

    return normalizedLicenseGroups;
  }

  renderLicenseDetails = () => {
    const { licenses, licensesStatus } = this.state;
    if (licensesStatus.loading) {
      return (
        <Loader />
      );
    }

    if (hasError(this.state.licensesStatus)) {
      return (
        <ErrorAlert status={this.state.licensesStatus} />
      );
    }

    return licenses.length > 0
      ? licenses.map(license => (
        <LicenseDetail key={license.licenseId} license={license} />
      ))
      : (
        <NoSummaryFound />
        );
  };

  renderMatches = () => {
    const { displayMode } = this.props;
    if (!displayMode) {
      // This is for TypeScript.
      return null;
    }

    let revisionOptions: SelectOption[] = [];
    if (displayMode.mode === 'multiple') {
      revisionOptions = _.uniq(displayMode.matchRevisionIds).map(revisionId => {
        const loc = Locator.parse(revisionId);
        return {
          label: `${renderPackage(loc)} (${renderRevision(loc)})`,
          value: revisionId,
        };
      });
    }

    const { selectedRevisionId, revisionLicensesStatus, matchesStatus } = this.state;

    let matchList: ReactElement | null = null;
    if (revisionLicensesStatus.loading || matchesStatus.loading) {
      matchList = (
        <Loader />
      );
    } else if (!hasError(revisionLicensesStatus) && !hasError(matchesStatus)) {
      matchList = (
        <div className="panel padded padded-small">
          <LicenseMatchList
            licenseGroups={this.filteredLicenseGroups}
            displayMode={displayMode.mode}
            matchLimit={50}
            onShowLicenseMatch={(group, filePath) => {
              this.setState({
                showingLicenseGroup: group,
                currentPath: filePath,
              });
            }}
          />
        </div>
      );
    }

    return (
      <>
        {hasError(revisionLicensesStatus) && (
          <ErrorAlert status={revisionLicensesStatus} />
        )}
        {hasError(matchesStatus) && (
          <ErrorAlert status={matchesStatus} />
        )}
        {displayMode.mode === 'single' ? (
          <h3>File Matches</h3>
        ) : (
          <div className="card">
            <b className="inline-header">License Found In {revisionOptions.length} Locations:</b>
            <Select
              options={revisionOptions}
              value={revisionOptions.find((opt) => opt.value === selectedRevisionId)}
              onChange={(e) => {
                if (e) {
                  this.onSelectRevision(e.value);
                }
              }}
            />
          </div>
        )}
        {matchList}
      </>
    );
  };

  renderMatchBrowser = () => (
    <ModalBrowseMatch
      cliLicenseScanWithMatchData={this.state.cliLicenseScanWithMatchData}
      showingLicenseGroup={this.state.showingLicenseGroup}
      currentPath={this.state.currentPath}
      revisionId={this.state.selectedRevisionId as string}
      packageCacheError={this.props.revisionPackageCacheError}
      ignoreMatchesStatus={this.state.ignoreMatchesStatus}
      onHide={this.onHideModalBrowseMatch}
      onPathClick={path => {
        this.setState({ currentPath: path });
      }}
      onBulkIgnore={this.onBulkIgnoreMatches}
      readOnly={!window.on_prem}
    />
  );

  render() {
    const { displayMode, onHide } = this.props;
    if (!displayMode) {
      return null;
    }

    const { licenses, tabState } = this.state;
    const license = _.head(licenses);

    if (displayMode.mode === 'single') {
      const hasCustomTitle = displayMode.revisionLicenses.some(({ licenseId, title }) => title && title !== licenseId);
      return (
        <Modal onHide={onHide} className="modal-horizontal" bsSize="large" show>
          <span className="btn-close-modal" onClick={onHide}>
            <Icon icon={icons.times} />
          </span>
          <div className="row">
            <div className="col-md-5 modal-horizontal-header">
              <div className="padded">
                <h4>{this.modalTitle}</h4>
                {hasCustomTitle && <p style={{ fontStyle: 'italic', marginTop: '5px' }}>License title was changed manually.</p>}
                {this.renderLicenseDetails()}
              </div>
            </div>
            <div className="col-md-7 modal-horizontal-content" style={{ height: '600px' }}>
              <ul className="nav nav-tabs margin-bottom-lg margin-top">
                <Tab
                  isActive={this.isTabActive(TabState.Matches)}
                  text="Matches"
                  onClick={this.onTabClick(TabState.Matches)}
                />
                <Tab
                  isActive={this.isTabActive(TabState.FullText)}
                  text="Full License Text"
                  onClick={this.onTabClick(TabState.FullText)}
                />
              </ul>
              {tabState === TabState.Matches && this.renderMatches()}
              {tabState === TabState.FullText && !!license && (
                <pre className="wrap-pre">{license.licenseText ? license.licenseText : 'No License Text Available For This License.'}</pre>
              )}
            </div>
            {this.renderMatchBrowser()}
          </div>
        </Modal>
      );
    }

    return (
      <ModalSlidePane onClose={onHide} width={66} show>
        <div className="modal-view-dependency-header">
          <div className="hero-unit">
            <h2>{this.modalTitle}</h2>
            <div>
              {this.renderLicenseDetails()}
            </div>
          </div>
        </div>
        <br />
        <div className="container-fluid">
          <ul className="nav nav-tabs margin-bottom-lg">
            <Tab
              isActive={this.isTabActive(TabState.Matches)}
              text="Matches"
              onClick={this.onTabClick(TabState.Matches)}
            />
            <Tab
              isActive={this.isTabActive(TabState.FullText)}
              text="Full License Text"
              onClick={this.onTabClick(TabState.FullText)}
            />
          </ul>
          {tabState === TabState.Matches && this.renderMatches()}
          {tabState === TabState.FullText && !!license && (
            <pre>{license.licenseText ? license.licenseText : 'No License Text Available For This License.'}</pre>
          )}
        </div>
        {this.renderMatchBrowser()}
      </ModalSlidePane>
    );
  }
}

export default ModalDisplayLicense;
